import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IChannel, IPoll, ITextFieldCollection } from "@Interfaces";
import { RootStateType } from "@Store";
import { compose } from "redux";
import { withDefaultLayout } from "@Hocs";
import { useParams } from "react-router-dom";
import {
    ASSISTANT_TAB,
    PARTICIPATION_TAB,
    POLLS_TAB,
    REPORT_TAB,
    ChannelHeader,
    ChannelTabsMenu,
    type ChannelTabValue,
} from "@Components";
import Grid from "@mui/material/Grid";
import { ChannelActions } from "@Store";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import useIsChannelAdmin from "@Hooks/useIsChannelAdmin";
import { PollsTab, PollsTabItem } from "./Tabs/PollsTab";
import { EventReportTab } from "./Tabs/EventReportTab";
import { AssistantTab } from "./Tabs/AssistantTab";
import { ParticipationTab } from "./Tabs/ParticipationTab";
import { buildPollsTabItems } from "../../Utils/componentOrderUtils";

const Home: React.FC<any> = () => {
    const dispatch = useDispatch<any>();
    const { channelId } = useParams<"channelId">();

    const [, setPolls] = useState<Array<IPoll>>([]);
    const [activeTab, setActiveTab] = useState<ChannelTabValue>(POLLS_TAB);
    const [, setTextFields] = useState<ITextFieldCollection[]>([]);
    const selectedChannel = useSelector((state: RootStateType) => {
        const fallbackChannel = state.channels.channels[0];
        const targetChannelId = channelId || fallbackChannel?._id;

        return state.channels.channels.find(
            (channel: IChannel) => channel._id === targetChannelId
        );
    });

    const isChannelAdmin = useIsChannelAdmin(selectedChannel);

    useEffect(() => {
        if (channelId) {
            dispatch(ChannelActions.ensureChannelData(channelId));
        }
    }, [channelId, dispatch]);

    useEffect(() => {
        if (selectedChannel) {
            setPolls(selectedChannel.polls ?? []);
        } else {
            setPolls([]);
        }
    }, [selectedChannel]);

    useEffect(() => {
        if (selectedChannel) {
            setTextFields(selectedChannel.text_fields ?? []);
        } else {
            setTextFields([]);
        }
    }, [selectedChannel]);

    const handleNewPollDialogOpen = () => {
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.PollCreatorDialog,
                props: {
                    channelId,
                    maxWidth: "md",
                },
            })
        );
    };

    const [items, setItems] = useState<PollsTabItem[]>([]);
    const currentChannelId = selectedChannel?._id ?? null;
    const pollsCount = selectedChannel?.polls.length ?? 0;
    const textFieldsCount = selectedChannel?.text_fields?.length ?? 0;
    const channelIdRef = useRef<string | null>(null);
    const pollsCountRef = useRef(0);
    const textFieldsCountRef = useRef(0);
    const componentOrderRef = useRef("");
    const componentOrderKey = (selectedChannel?.componentOrder ?? [])
        .map((entry) => `${entry.type}:${entry.id}`)
        .join("");
    const itemsInitializedRef = useRef(false);

    useEffect(() => {
        if (!selectedChannel) {
            setPolls([]);
            setTextFields([]);
            setItems([]);
            channelIdRef.current = null;
            pollsCountRef.current = 0;
            textFieldsCountRef.current = 0;
            itemsInitializedRef.current = false;
            componentOrderRef.current = "";
            return;
        }

        const pollsData = selectedChannel.polls ?? [];
        const textFieldsData = selectedChannel.text_fields ?? [];
        setPolls(pollsData);
        setTextFields(textFieldsData);

        const shouldResetItems =
            channelIdRef.current !== currentChannelId ||
            pollsCountRef.current !== pollsCount ||
            textFieldsCountRef.current !== textFieldsCount ||
            componentOrderRef.current !== componentOrderKey ||
            !itemsInitializedRef.current;

        if (shouldResetItems) {
            channelIdRef.current = currentChannelId;
            pollsCountRef.current = pollsCount;
            textFieldsCountRef.current = textFieldsCount;
            componentOrderRef.current = componentOrderKey;
            itemsInitializedRef.current = true;

            const nextItems = buildPollsTabItems(
                pollsData,
                textFieldsData,
                selectedChannel?.componentOrder
            );

            setItems(nextItems);
        }
    }, [
        selectedChannel,
        currentChannelId,
        pollsCount,
        textFieldsCount,
        componentOrderKey,
    ]);

    const handleTextFieldEdit = (textFieldId: string, question: string) => {
        if (!selectedChannel) return Promise.resolve();

        return dispatch(
            ChannelActions.updatePollTextField(
                textFieldId,
                selectedChannel._id,
                question
            ) as any
        );
    };

    const handleTextFieldDelete = (textFieldId: string) => {
        if (!selectedChannel) return;

        dispatch(
            ChannelActions.deletePollTextField(textFieldId, selectedChannel._id)
        );
    };

    const handleTabChange = (tabValue: ChannelTabValue) => {
        setActiveTab(tabValue);
    };

    const renderTabContent = () => {
        const canEditClosedChannelContent = !selectedChannel?.closed;

        switch (activeTab) {
            case POLLS_TAB:
                return (
                    <PollsTab
                        items={items}
                        onCreateNewPoll={handleNewPollDialogOpen}
                        selectedChannel={selectedChannel}
                        onTextFieldEdit={
                            canEditClosedChannelContent
                                ? handleTextFieldEdit
                                : undefined
                        }
                        onTextFieldDelete={
                            canEditClosedChannelContent
                                ? handleTextFieldDelete
                                : undefined
                        }
                        isLoading={selectedChannel?.pollsStatus === "loading"}
                        textFields={selectedChannel?.text_fields}
                        canAddPoll={canEditClosedChannelContent}
                        isChannelAdmin={isChannelAdmin}
                    />
                );
            case PARTICIPATION_TAB:
                return (
                    <Grid item xs={12}>
                        <ParticipationTab channel={selectedChannel} />
                    </Grid>
                );
            case REPORT_TAB:
                return selectedChannel ? (
                    <Grid item xs={12}>
                        <EventReportTab channel={selectedChannel} />
                    </Grid>
                ) : null;
            case ASSISTANT_TAB:
                return (
                    <Grid item xs={12}>
                        <AssistantTab
                            channelId={channelId || selectedChannel?._id || ""}
                            eventTitle={selectedChannel?.title || ""}
                        />
                    </Grid>
                );
            default:
                return (
                    <PollsTab
                        items={items}
                        onCreateNewPoll={handleNewPollDialogOpen}
                        selectedChannel={selectedChannel}
                        onTextFieldEdit={
                            canEditClosedChannelContent
                                ? handleTextFieldEdit
                                : undefined
                        }
                        onTextFieldDelete={
                            canEditClosedChannelContent
                                ? handleTextFieldDelete
                                : undefined
                        }
                        isLoading={selectedChannel?.pollsStatus === "loading"}
                        textFields={selectedChannel?.text_fields}
                        canAddPoll={canEditClosedChannelContent}
                        isChannelAdmin={isChannelAdmin}
                    />
                );
        }
    };

    return (
        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
        >
            {selectedChannel && (
                <Grid item xs={12}>
                    <ChannelHeader {...selectedChannel} showPresentMode />
                    <ChannelTabsMenu
                        value={activeTab}
                        onChange={handleTabChange}
                        hasEventReport={Boolean(
                            selectedChannel?.executive_summary
                        )}
                    />
                </Grid>
            )}
            {renderTabContent()}
        </Grid>
    );
};

export default compose(withDefaultLayout)(Home);
